// src/services/taskAnalysisService.js
export class TaskAnalysisService {
  // Extract detailed task structure from questions array
  static extractTaskStructure(task) {
    if (!task.questions || !Array.isArray(task.questions)) {
      return {
        topics: task.topics || [],
        totalMarks: task.totalMarks || 0,
        topicBreakdown: {},
        subtopicBreakdown: {},
        conceptBreakdown: {},
        skillsBreakdown: {},
        taxonomyBreakdown: {}
      };
    }

    const topicBreakdown = {};
    const subtopicBreakdown = {};
    const conceptBreakdown = {};
    const skillsBreakdown = {};
    const taxonomyBreakdown = {};

    // Process each question
    task.questions.forEach(question => {
      this.processQuestion(question, {
        topicBreakdown,
        subtopicBreakdown,
        conceptBreakdown,
        skillsBreakdown,
        taxonomyBreakdown
      });
    });

    return {
      topics: task.topics || [],
      totalMarks: task.totalMarks || 0,
      questionCount: task.questions.length,
      topicBreakdown,
      subtopicBreakdown,
      conceptBreakdown,
      skillsBreakdown,
      taxonomyBreakdown,
      taskType: task.type,
      papers: task.papers || []
    };
  }

  static processQuestion(question, breakdowns) {
    const {
      topicBreakdown,
      subtopicBreakdown,
      conceptBreakdown,
      skillsBreakdown,
      taxonomyBreakdown
    } = breakdowns;

    // Process main question
    if (question.topic) {
      this.addToBreakdown(topicBreakdown, question.topic, question.marks || 0, question);
    }
    
    if (question.subtopic) {
      this.addToBreakdown(subtopicBreakdown, question.subtopic, question.marks || 0, question);
    }

    if (question.concepts && Array.isArray(question.concepts)) {
      question.concepts.forEach(concept => {
        this.addToBreakdown(conceptBreakdown, concept, question.marks || 0, question);
      });
    }

    if (question.skills && Array.isArray(question.skills)) {
      question.skills.forEach(skill => {
        this.addToBreakdown(skillsBreakdown, skill, question.marks || 0, question);
      });
    }

    if (question.taxonomyLevel) {
      this.addToBreakdown(taxonomyBreakdown, question.taxonomyLevel, question.marks || 0, question);
    }

    // Process subquestions recursively
    if (question.subquestions && Array.isArray(question.subquestions)) {
      question.subquestions.forEach(subquestion => {
        this.processQuestion(subquestion, breakdowns);
      });
    }
  }

  static addToBreakdown(breakdown, key, marks, question) {
    if (!breakdown[key]) {
      breakdown[key] = {
        totalMarks: 0,
        questionCount: 0,
        questions: [],
        percentage: 0
      };
    }
    
    breakdown[key].totalMarks += marks;
    breakdown[key].questionCount += 1;
    breakdown[key].questions.push({
      questionNumber: question.questionNumber,
      marks: marks,
      questionType: question.questionType,
      taxonomyLevel: question.taxonomyLevel
    });
  }

  // Calculate percentages after all processing
  static finalizeBreakdowns(taskStructure) {
    const { totalMarks } = taskStructure;
    
    Object.keys(taskStructure).forEach(breakdownKey => {
      if (breakdownKey.includes('Breakdown')) {
        const breakdown = taskStructure[breakdownKey];
        Object.keys(breakdown).forEach(key => {
          breakdown[key].percentage = totalMarks > 0 
            ? (breakdown[key].totalMarks / totalMarks) * 100 
            : 0;
        });
      }
    });

    return taskStructure;
  }

  // Analyze learner performance by topic/subtopic
  static analyzeLearnerPerformanceByTopic(marks, taskStructure) {
    const topicPerformance = {};
    const subtopicPerformance = {};
    const conceptPerformance = {};

    // Group marks by itemised breakdown if available
    if (marks.itemisedMarks && Array.isArray(marks.itemisedMarks)) {
      marks.itemisedMarks.forEach(item => {
        // Find corresponding question in task structure
        const question = this.findQuestionByNumber(taskStructure, item.questionNumber);
        if (question) {
          // Calculate performance for this question item
          const performance = item.totalMarks > 0 ? (item.achievedMarks / item.totalMarks) * 100 : 0;
          
          // Add to topic performance
          if (question.topic) {
            if (!topicPerformance[question.topic]) {
              topicPerformance[question.topic] = { total: 0, achieved: 0, count: 0 };
            }
            topicPerformance[question.topic].total += item.totalMarks;
            topicPerformance[question.topic].achieved += item.achievedMarks;
            topicPerformance[question.topic].count += 1;
          }

          // Add to subtopic performance
          if (question.subtopic) {
            if (!subtopicPerformance[question.subtopic]) {
              subtopicPerformance[question.subtopic] = { total: 0, achieved: 0, count: 0 };
            }
            subtopicPerformance[question.subtopic].total += item.totalMarks;
            subtopicPerformance[question.subtopic].achieved += item.achievedMarks;
            subtopicPerformance[question.subtopic].count += 1;
          }

          // Add to concept performance
          if (question.concepts && Array.isArray(question.concepts)) {
            question.concepts.forEach(concept => {
              if (!conceptPerformance[concept]) {
                conceptPerformance[concept] = { total: 0, achieved: 0, count: 0 };
              }
              conceptPerformance[concept].total += item.totalMarks / question.concepts.length;
              conceptPerformance[concept].achieved += item.achievedMarks / question.concepts.length;
              conceptPerformance[concept].count += 1;
            });
          }
        }
      });
    }

    // Calculate percentages
    const calculatePercentages = (performanceObj) => {
      const result = {};
      Object.keys(performanceObj).forEach(key => {
        const perf = performanceObj[key];
        result[key] = {
          percentage: perf.total > 0 ? (perf.achieved / perf.total) * 100 : 0,
          totalMarks: perf.total,
          achievedMarks: perf.achieved,
          questionCount: perf.count
        };
      });
      return result;
    };

    return {
      topicPerformance: calculatePercentages(topicPerformance),
      subtopicPerformance: calculatePercentages(subtopicPerformance),
      conceptPerformance: calculatePercentages(conceptPerformance)
    };
  }

  static findQuestionByNumber(taskStructure, questionNumber) {
    // Recursively search through questions and subquestions
    const searchInQuestions = (questions) => {
      for (const question of questions) {
        if (question.questionNumber === questionNumber) {
          return question;
        }
        if (question.subquestions && Array.isArray(question.subquestions)) {
          const found = searchInQuestions(question.subquestions);
          if (found) return found;
        }
      }
      return null;
    };

    return searchInQuestions(taskStructure.questions || []);
  }

  // Compare two tasks at topic/subtopic level
  static compareTaskStructures(task1Structure, task2Structure) {
    const comparison = {
      topicComparison: {},
      subtopicComparison: {},
      conceptComparison: {},
      marksAllocation: {},
      difficultyComparison: {}
    };

    // Compare topics
    const allTopics = new Set([
      ...Object.keys(task1Structure.topicBreakdown),
      ...Object.keys(task2Structure.topicBreakdown)
    ]);

    allTopics.forEach(topic => {
      const task1Topic = task1Structure.topicBreakdown[topic];
      const task2Topic = task2Structure.topicBreakdown[topic];
      
      comparison.topicComparison[topic] = {
        task1Marks: task1Topic?.totalMarks || 0,
        task2Marks: task2Topic?.totalMarks || 0,
        task1Percentage: task1Topic?.percentage || 0,
        task2Percentage: task2Topic?.percentage || 0,
        marksDifference: (task2Topic?.totalMarks || 0) - (task1Topic?.totalMarks || 0),
        percentageDifference: (task2Topic?.percentage || 0) - (task1Topic?.percentage || 0)
      };
    });

    // Compare subtopics
    const allSubtopics = new Set([
      ...Object.keys(task1Structure.subtopicBreakdown),
      ...Object.keys(task2Structure.subtopicBreakdown)
    ]);

    allSubtopics.forEach(subtopic => {
      const task1Subtopic = task1Structure.subtopicBreakdown[subtopic];
      const task2Subtopic = task2Structure.subtopicBreakdown[subtopic];
      
      comparison.subtopicComparison[subtopic] = {
        task1Marks: task1Subtopic?.totalMarks || 0,
        task2Marks: task2Subtopic?.totalMarks || 0,
        task1Percentage: task1Subtopic?.percentage || 0,
        task2Percentage: task2Subtopic?.percentage || 0,
        marksDifference: (task2Subtopic?.totalMarks || 0) - (task1Subtopic?.totalMarks || 0),
        percentageDifference: (task2Subtopic?.percentage || 0) - (task1Subtopic?.percentage || 0)
      };
    });

    // Compare taxonomy levels (difficulty)
    const allTaxonomyLevels = new Set([
      ...Object.keys(task1Structure.taxonomyBreakdown),
      ...Object.keys(task2Structure.taxonomyBreakdown)
    ]);

    allTaxonomyLevels.forEach(level => {
      const task1Level = task1Structure.taxonomyBreakdown[level];
      const task2Level = task2Structure.taxonomyBreakdown[level];
      
      comparison.difficultyComparison[level] = {
        task1Marks: task1Level?.totalMarks || 0,
        task2Marks: task2Level?.totalMarks || 0,
        task1Percentage: task1Level?.percentage || 0,
        task2Percentage: task2Level?.percentage || 0,
        marksDifference: (task2Level?.totalMarks || 0) - (task1Level?.totalMarks || 0),
        percentageDifference: (task2Level?.percentage || 0) - (task1Level?.percentage || 0)
      };
    });

    return comparison;
  }
}